import { useEffect, useState } from "react";
import { usePlay } from "@/providers/play-provider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Lock, Users, Clock, Plus, RefreshCw } from "lucide-react";
import { formatRelativeTime } from "@/lib/date-tools";
import { useTranslation } from "@/providers/language-provider";

import {
  GameMetadata,
  getGameTypeLabel,
} from "@/pages/subs/play/common/game-registry";

export const LobbyView = ({ games }: { games: GameMetadata[] }) => {
  const { language } = useTranslation();
  const {
    isLobbySubscribed,
    gameRooms,
    subscribeLobby,
    unsubscribeLobby,
    getRoomList,
    createGameRoom,
    joinGameRoom,
  } = usePlay();

  const [selectedGameType, setSelectedGameType] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [joinPassword, setJoinPassword] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState("");

  // 게임룸 생성 폼 상태
  const [createForm, setCreateForm] = useState({
    gameType: "None",
    maxPlayers: 1,
    password: "",
    roomTitle: "",
  });

  useEffect(() => {
    if (!isLobbySubscribed) {
      subscribeLobby();
    }
    return () => {
      unsubscribeLobby();
    };
  }, []);

  useEffect(() => {
    if (games?.length > 0) {
      setCreateForm((prev) => ({
        ...prev,
        gameType: games[0].id,
        maxPlayers: games[0].maxPlayers,
      }));
    }
  }, [games]);

  useEffect(() => {
    if (isLobbySubscribed) {
      getRoomList(selectedGameType === "all" ? undefined : selectedGameType);
    }
  }, [isLobbySubscribed, selectedGameType]);

  const handleCreateRoom = () => {
    createGameRoom(
      createForm.gameType,
      createForm.maxPlayers,
      createForm.password || undefined,
      createForm.roomTitle || undefined, // 추가
    );
    setIsCreateDialogOpen(false);
    setCreateForm({
      gameType: games[0].id,
      maxPlayers: games[0].maxPlayers,
      password: "",
      roomTitle: "", // 추가
    });
  };

  const handleJoinRoom = (roomId: string, hasPassword: boolean) => {
    if (hasPassword) {
      setSelectedRoomId(roomId);
    } else {
      joinGameRoom(roomId);
    }
  };

  const handleJoinWithPassword = () => {
    if (selectedRoomId && joinPassword) {
      joinGameRoom(selectedRoomId, joinPassword);
      setJoinPassword("");
      setSelectedRoomId("");
    }
  };

  const filteredRooms = gameRooms.filter(
    (room) => selectedGameType === "all" || room.gameType === selectedGameType,
  );

  // const getGameTypeLabel = (gameType: string) => {
  //   const game = GAME_TYPES.find((g) => g.value === gameType);
  //   return game ? `${game.icon} ${game.label}` : gameType;
  // };

  const getStatusBadge = (status: string) => {
    const variants: Record<
      string,
      "default" | "secondary" | "destructive" | "outline"
    > = {
      waiting: "default",
      playing: "secondary",
      finished: "outline",
    };

    const labels: Record<string, string> = {
      waiting: "대기중",
      playing: "게임중",
      finished: "완료됨",
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="w-full h-full p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">🎮 게임 로비</h1>
            <p className="text-muted-foreground">
              다른 플레이어들과 함께 게임을 즐겨보세요
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                getRoomList(
                  selectedGameType === "all" ? undefined : selectedGameType,
                )
              }
              disabled={!isLobbySubscribed}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              새로고침
            </Button>
            <Dialog
              open={isCreateDialogOpen}
              onOpenChange={setIsCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  게임방 만들기
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>새 게임방 만들기</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="roomTitle">방 제목</Label>
                    <Input
                      id="roomTitle"
                      type="text"
                      value={createForm.roomTitle}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          roomTitle: e.target.value,
                        }))
                      }
                      placeholder="방 제목을 입력하세요 (선택사항)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gameType">게임 종류</Label>
                    <Select
                      value={createForm.gameType}
                      onValueChange={(value) => {
                        const game = games.find((g) => g.id === value);
                        setCreateForm((prev) => ({
                          ...prev,
                          gameType: value,
                          maxPlayers: game ? game.maxPlayers : prev.maxPlayers,
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="게임 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        {games.map((game) => (
                          <SelectItem key={game.id} value={game.id}>
                            {game.icon} {game.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="maxPlayers">최대 플레이어 수</Label>
                    <Select
                      value={createForm.maxPlayers.toString()}
                      onValueChange={(value) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          maxPlayers: parseInt(value),
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num}명
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="password">비밀번호 (선택사항)</Label>
                    <Input
                      id="password"
                      type="password"
                      value={createForm.password}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      placeholder="비밀번호 없이 만들려면 비워두세요"
                    />
                  </div>
                  <Button onClick={handleCreateRoom} className="w-full">
                    게임방 만들기
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <Select value={selectedGameType} onValueChange={setSelectedGameType}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">🎯 모든 게임</SelectItem>
              {games?.map((game) => (
                <SelectItem key={game.id} value={game.id}>
                  {game.icon} {game.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!isLobbySubscribed ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">로비에 연결하는 중...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRooms.map((room) => (
              <Card
                key={room.roomId}
                className="hover:shadow-md transition-shadow"
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">
                      {room.roomTitle || getGameTypeLabel(room.gameType)}
                    </CardTitle>
                    {getStatusBadge(room.status)}
                  </div>
                  <CardDescription className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {room.currentPlayers}/{room.maxPlayers}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatRelativeTime(
                        room.createdAt.toString(),
                        language as "ko-KR" | "en-US",
                      )}
                    </span>
                    {room.hasPassword && <Lock className="w-4 h-4" />}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium">호스트</p>
                      <p className="text-sm text-muted-foreground">
                        {room.hostName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">참여자</p>
                      <p className="text-sm text-muted-foreground">
                        {room.playerNames?.join(", ") || "정보 없음"}
                      </p>
                    </div>
                    <Button
                      onClick={() =>
                        handleJoinRoom(room.roomId, room.hasPassword)
                      }
                      disabled={
                        room.status !== "waiting" ||
                        room.currentPlayers >= room.maxPlayers
                      }
                      size="sm"
                    >
                      참여하기
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {isLobbySubscribed && filteredRooms.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              현재 참여할 수 있는 게임방이 없습니다.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />첫 번째 게임방 만들기
            </Button>
          </div>
        )}

        {/* 비밀번호 입력 다이얼로그 */}
        <Dialog
          open={!!selectedRoomId}
          onOpenChange={() => setSelectedRoomId("")}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>비밀번호 입력</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="roomPassword">게임방 비밀번호</Label>
                <Input
                  id="roomPassword"
                  type="password"
                  value={joinPassword}
                  onChange={(e) => setJoinPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleJoinWithPassword();
                    }
                  }}
                />
              </div>
              <Button onClick={handleJoinWithPassword} className="w-full">
                게임방 참여
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
